import { Check, FileSignature, KeyRound, LockKeyhole } from 'lucide-react';
import { type CSSProperties } from 'react';

import lawyerUrl from '@/assets/landing/advogada-laptop.webp';
import avatar2 from '@/assets/landing/avatar-2.webp';
import avatar3 from '@/assets/landing/avatar-3.webp';
import teamUrl from '@/assets/landing/equipe-escritorio.webp';

import { SectionLabel } from './section-label';

const steps = [
  [
    '01',
    'Documentos que se preenchem',
    'Modelos com os dados do cliente e do processo, em DOCX ou PDF, um a um ou em lote.',
  ],
  [
    '02',
    'Assinatura sem sair do caso',
    'Signatários em paralelo ou em ordem, e o PDF assinado volta sozinho para os documentos.',
  ],
  [
    '03',
    'Cliente informado',
    'Pelo portal, o cliente acompanha processos, audiências, documentos e faturas.',
  ],
] as const;

const controls = [
  'Dados de cada escritório isolados, até no banco de dados',
  'Verificação em duas etapas para sócios e administradores',
  'Permissões por perfil: sócio, advogado, estagiário, secretária',
  'Trilha de auditoria imutável',
  'Antivírus e criptografia nos documentos',
  'Pedidos de titulares e prazos de guarda da LGPD',
];

/**
 * A forma de aba de pasta usada pelas fotos (`.lp-folder`). Em
 * objectBoundingBox, desenhada para a proporção 4:3 — com outra proporção os
 * cantos deformam.
 */
export function FolderClipDefs() {
  return (
    <svg width="0" height="0" aria-hidden="true" className="absolute">
      <clipPath id="lp-folder-clip" clipPathUnits="objectBoundingBox">
        <path d="M0,0.0667 Q0,0 0.05,0 L0.34,0 Q0.37,0 0.385,0.03 L0.4,0.06 Q0.413,0.085 0.445,0.085 L0.95,0.085 Q1,0.085 1,0.152 L1,0.9333 Q1,1 0.95,1 L0.05,1 Q0,1 0,0.9333 Z" />
      </clipPath>
    </svg>
  );
}

export function DocumentsStory() {
  return (
    <section aria-labelledby="lp-documents-title" className="bg-white pb-24 sm:pb-32">
      <div className="mx-auto grid max-w-[1180px] items-center gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <div className="relative" data-reveal>
          <img
            src={teamUrl}
            alt="Equipe de um escritório trabalhando junta em notebooks"
            width="1600"
            height="1067"
            loading="lazy"
            className="lp-folder w-full object-cover"
          />
          <div className="lp-float absolute bottom-6 right-4 flex items-center gap-3 rounded-2xl bg-white/95 p-3 pr-4 shadow-[0_24px_50px_-24px_rgb(11_13_18/0.5)] backdrop-blur xl:-right-6">
            <span className="bg-tertiary-fixed text-on-tertiary-fixed-variant grid size-10 place-items-center rounded-xl">
              <FileSignature size={18} />
            </span>
            <span className="text-[13px] leading-tight">
              <strong className="text-(--lp-ink) block font-medium">Contrato de honorários</strong>
              <span className="text-(--lp-body)">Assinado por 2 de 2</span>
            </span>
            <span className="ml-2 flex -space-x-2">
              {[avatar2, avatar3].map((src) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  width="28"
                  height="28"
                  loading="lazy"
                  className="size-7 rounded-full border-2 border-white object-cover"
                />
              ))}
            </span>
          </div>
        </div>

        <div>
          <div data-reveal>
            <SectionLabel>Documentos e clientes</SectionLabel>
            <h2
              id="lp-documents-title"
              className="text-(--lp-mute) mt-5 text-balance text-[clamp(32px,4.2vw,48px)] font-medium leading-[1.08] tracking-[-0.04em]"
            >
              Menos tempo em papelada.{' '}
              <span className="text-(--lp-ink)">Mais tempo com o cliente.</span>
            </h2>
          </div>
          <ol className="divide-(--lp-line) border-(--lp-line) mt-10 divide-y border-y">
            {steps.map(([number, title, text], index) => (
              <li
                key={number}
                data-reveal
                style={{ '--delay': index } as CSSProperties}
                className="grid grid-cols-[44px_1fr] gap-4 py-5"
              >
                <span className="text-(--lp-blue) pt-0.5 text-[13px] font-medium">{number}</span>
                <span>
                  <strong className="text-(--lp-ink) block text-[17px] font-medium tracking-[-0.01em]">
                    {title}
                  </strong>
                  <span className="text-(--lp-body) mt-1 block text-[15px] leading-relaxed">
                    {text}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

export function SecurityStory() {
  return (
    <section
      id="seguranca"
      aria-labelledby="lp-security-title"
      className="scroll-mt-20 bg-white py-24 sm:py-32"
    >
      <div className="mx-auto grid max-w-[1180px] items-center gap-12 px-5 sm:px-6 lg:grid-cols-2 lg:gap-16">
        <div className="lg:order-2" data-reveal>
          <div className="relative">
            <img
              src={lawyerUrl}
              alt="Advogada trabalhando no notebook em um escritório"
              width="1600"
              height="900"
              loading="lazy"
              className="lp-folder w-full object-cover"
            />
            <div className="lp-float absolute left-4 top-[22%] flex items-center gap-2.5 rounded-2xl bg-white/95 px-3.5 py-2.5 text-[13px] shadow-[0_24px_50px_-24px_rgb(11_13_18/0.5)] backdrop-blur xl:-left-6">
              <KeyRound size={16} className="text-(--lp-blue)" />
              <span className="text-(--lp-ink) font-medium">Verificação em duas etapas</span>
              <span className="bg-tertiary-fixed text-on-tertiary-fixed-variant rounded-md px-2 py-0.5 text-[11px] font-medium">
                Ativa
              </span>
            </div>
            <div className="lp-float absolute bottom-[14%] right-4 flex items-center gap-2.5 rounded-2xl bg-white/95 px-3.5 py-2.5 text-[13px] shadow-[0_24px_50px_-24px_rgb(11_13_18/0.5)] backdrop-blur [animation-delay:-3s] xl:-right-6">
              <LockKeyhole size={16} className="text-(--lp-blue)" />
              <span className="text-(--lp-ink) font-medium">Dados isolados por escritório</span>
            </div>
          </div>
        </div>

        <div>
          <div data-reveal>
            <SectionLabel>Segurança e sigilo</SectionLabel>
            <h2
              id="lp-security-title"
              className="text-(--lp-mute) mt-5 text-balance text-[clamp(32px,4.2vw,48px)] font-medium leading-[1.08] tracking-[-0.04em]"
            >
              Sigilo profissional levado a sério.{' '}
              <span className="text-(--lp-ink)">Por arquitetura.</span>
            </h2>
            <p className="text-(--lp-body) mt-5 max-w-[520px] text-[17px] leading-relaxed">
              Dados de cliente e de processo são confidenciais por lei. O Sancus foi construído a
              partir disso, e não adaptado depois.
            </p>
          </div>
          <ul className="mt-8 grid gap-x-6 gap-y-3.5 sm:grid-cols-2" data-reveal>
            {controls.map((control) => (
              <li key={control} className="text-(--lp-ink) flex gap-2.5 text-[15px] leading-snug">
                <Check size={17} className="text-(--lp-blue) mt-0.5 shrink-0" />
                {control}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
